<template>
  <component
    :is="design === 'b2c' ? GenericAccordion : 'div'"
    v-if="shouldShow"
    :headline="$t('productPage.safetyInfo')"
    size="xl"
    start-open
    class="SafetyInformation type-sm"
  >
    <div class="type-tagline-xs py-16">{{ $t('productPage.safetyInfo') }}</div>
    <div class="desk:grid grid-cols-2 border-t border-border-subtle">
      <div
        v-for="item in safetyItems"
        :key="item.code"
        class="border-b  border-border-subtle py-24
               desk:odd:pr-48 desk:odd:border-r desk:even:pl-48"
      >
        <div class="type-headline-sm mb-8">{{ item.headline }}</div>
        <div
          v-for="(value, vi) in item.values"
          :key="value.code || value.translation"
          class="mb-4 last:mb-0"
          :class="{
            'inline': showCommaSeparated.includes(item.code)
          }"
        >
          <nuxt-img
            v-if="value.image"
            :src="value.image"
            :alt="value.translation"
            provider="norce"
            height="40"
            loading="lazy"
            class="h-40"
          />
          <template v-else>
            <div
              v-if="value.code && value.code !== 'Warning' && !showCommaSeparated.includes(item.code)"
              class="text-text-inactive inline-block mr-8"
              v-html="value.code"
            />
            <div
              class="break-words"
              :class="{
                'py-4 px-8  inline-block font-semibold bg-bg-error text-text-warning': value.code && value.code === 'Warning'
              }"
              v-html="value.translation + (showCommaSeparated.includes(item.code) && vi + 1 < item.values.length ? ', ' : '')"
            />
          </template>
        </div>
      </div>

      <div
        v-if="sdsFiles.length"
        class="border-b border-border-subtle py-24
               desk:odd:pr-48 desk:odd:border-r even:pl-48"
      >
        <div class="type-headline-sm mb-8">{{ sdsTitle }}</div>
        <a
          v-for="file in sdsFiles"
          :key="file.url"
          :href="`/filedownload/?origin=${file.url}&mimeType=${file.mimeType}`"
          target="_blank"
          class="flex items-center gap-8 py-4 type-sm underline"
        >
          <img
            src="/icons/file-download.svg"
            alt=""
            class="w-12 shrink-0"
          >
          {{ file.name }}
        </a>
      </div>
      <div
        v-if="isUneven"
        class="border-b border-border-subtle mobOnly:hidden"
      />
    </div>
  </component>
</template>

<script setup lang="ts">
import type { ProductModel } from '#models/product';
import type { ProductVariantModel } from '#models/productVariant';
import type { ParametricData } from '#constants/types/algolia';
import { useGlobalContentStore } from '~/store/globalContent';
import { NorceMediaSetCodes } from '#constants/norceCodes';

const props = defineProps<{
  productItem: ProductModel,
  activeVariant: ProductVariantModel,
  design: 'b2b' | 'b2c', // default b2b
}>();

const globalContent = useGlobalContentStore();
const GenericAccordion = resolveComponent('GenericAccordion');

const safetyParametricCodes = [
  'HazardPictograms',
  'SignalWord',
  'HazardStatements',
  'PrecautionaryStatements',
  'ItemIngredients',
];

// Hide codes and displays comma separated
const showCommaSeparated = [
  'ItemIngredients',
];

function getParametric(code: string): ParametricData | undefined {
  return (props.activeVariant.parametrics as ParametricData[])?.find(p => p.code === code)
    ?? props.productItem.parametrics?.find(p => p.code === code);
}

function getFilterValue(parametricCode: string, valueCode: string): { translation: string; image: string | null } {
  const fv = globalContent.filterSettings.filterValues.find(
    f => f.code === `parametricFilters.${parametricCode}.${valueCode}`
  );
  return {
    translation: fv?.name ?? valueCode,
    image: fv?.image ? `${globalContent.getNorceCdnBaseUrl}/${fv.image}` : null,
  };
}

const sdsMediaSet = computed(() => {
  const combined = [...(props.activeVariant.mediaSets || [])];
  (props.productItem.mediaSets || []).forEach(ms => {
    const existingSet = combined.find(c => c.code === ms.code);
    if (existingSet) {
      ms.media?.forEach(vm => {
        if (existingSet.media && !existingSet.media.find(f => f.url === vm.url)) {
          existingSet.media.push(vm);
        }
      });
    } else {
      combined.push(ms);
    }
  });
  return combined.find(ms => ms.code === NorceMediaSetCodes.SDS) ?? null;
});

const sdsTitle = computed(() => sdsMediaSet.value?.title || 'SDS');

const sdsFiles = computed(() =>
  (sdsMediaSet.value?.media || []).filter(f => f.name?.length > 0)
);

const safetyItems = computed(() => {
  return safetyParametricCodes.map(code => {
    const parametric = getParametric(code);
    if (!parametric) return null;

    const headline = globalContent.filterSettings.filters.find(
      f => f.code === `parametricFilters.${code}`
    )?.name ?? code;

    let values: { code: string; translation: string; image: string | null }[] = [];

    if (parametric.type === 'multiple' && parametric.multipleValues?.length) {
      values = parametric.multipleValues.map(mv => ({
        code: mv.code,
        ...getFilterValue(code, mv.code),
      }));
    } else if (parametric.type === 'list' && parametric.listValue) {
      values = [{
        code: parametric.listValue.code,
        ...getFilterValue(code, parametric.listValue.code),
      }];
    } else if (parametric.value != null) {
      values = [{
        code: '',
        translation: String(parametric.value),
        image: null,
      }];
    }

    if (!values.length) return null;

    return { code, headline, values };
  }).filter(item => item !== null) as { code: string; headline: string; values: { code: string; translation: string; image: string | null }[] }[];
});

const isUneven = computed(()=> {
  return (safetyItems.value.length + (sdsFiles.value.length ? 1 : 0)) % 2 !== 0;
});

// Only show on UK/DE/SE
const showInMarket = ['gb', 'se', 'de'];

const shouldShow = computed(()=> {
  return (safetyItems.value.length || sdsFiles.value.length) && showInMarket.includes(globalContent.getCountryCode);
});

</script>

<style scoped lang="postcss">
</style>
